import type { LessonsIndexData } from '../../model/lessons/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { getLessonsFilterParams } from '../../lib/lessons-query'
import { useLessonsStore } from '../../model/lessons/store'

interface LessonsApiOptions {
  disciplineId: string | number
}

/** Filters and pages change faster than the list answers, so only the latest request may land. */
let activeRequest: AbortController | null = null

export const abortLessonsApi = (): void => {
  activeRequest?.abort()
  activeRequest = null
  useLessonsStore().setLoading(false)
}

/** `GET /admin-api/disciplines/{discipline}/lessons` — one page of the discipline's lessons. */
export const useLessonsApi = async (options: LessonsApiOptions): Promise<LessonsIndexData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const store = useLessonsStore()

  const url = useAdminRoutes('admin.lessons.api.index', pick({
    discipline_id: options.disciplineId,
    ...getLessonsFilterParams(store.getFilters),
    per_page: store.getPerPage,
    page: store.getCurrentPage,
  }))

  activeRequest?.abort()
  const request = new AbortController()
  activeRequest = request

  store.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url, { signal: request.signal })

    if (request.signal.aborted) return null

    const indexData: LessonsIndexData | null = data.data || null

    if (indexData) {
      store.setLessons(indexData.lessons)
      store.setMeta(indexData.meta)
      store.setSlots(indexData.booking_slots)
    }

    return indexData
  } catch (err: unknown) {
    if (request.signal.aborted) return null

    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    if (activeRequest === request) {
      activeRequest = null
      store.setLoading(false)
    }
  }
}
